import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Can } from '../../contexts/PermissionsContext'
import { UserSelect } from '../UserSelect'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { EmptyState } from '../ui/empty-state'
import { apiErrorMessage, useAssemblyAttendance, useAssemblyMutations, useAttendancePreview } from './assembly-api'
import { PRESENCES } from './assembly-utils'

/**
 * The attendance sheet: one line per co-owner with their voices and lots. It is
 * built from the owners and the lots' ownership_shares, can be adjusted by hand
 * (a paper minutes' sheet, an owner the data does not know), and freezes
 * total_voices / total_members in the minutes. Spec 2026-10-05-01 §5.
 */
export function AttendanceTab({ org, minutesUid, status }: { org: string; minutesUid: string; status: string }) {
    const { t } = useTranslation('assemblies')
    const { data: sheet, isLoading } = useAssemblyAttendance(org, minutesUid)
    const { generateAttendance, addParty, removeParty, setPresence, changeHolder, waiveBase } = useAssemblyMutations(org)
    const [holderOf, setHolderOf] = useState<string | null>(null)
    const [newHolder, setNewHolder] = useState('')
    const [userUid, setUserUid] = useState('')
    const [voices, setVoices] = useState('')
    const [removeTarget, setRemoveTarget] = useState<{ uid: string; name: string } | null>(null)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [reason, setReason] = useState('')
    const { data: preview } = useAttendancePreview(org, minutesUid, previewOpen)
    const convened = status !== 'draft' && (sheet as { mode?: string } | undefined)?.mode !== 'recorded'
    const frozen = (sheet?.frozen ?? (status === 'in_progress' || status === 'closed')) || convened
    const path = { orgCode: org, contractUid: minutesUid }
    const holderError = apiErrorMessage(changeHolder.error)
    const error = apiErrorMessage(
        generateAttendance.error || addParty.error || removeParty.error || setPresence.error || waiveBase.error,
    )

    if (isLoading || !sheet) return <p className="text-sm text-muted-foreground">{t('loading')}</p>

    const base = sheet.base
    const onSheet = sheet.parties.some((p) => p.user_uid === userUid)

    return (
        <div className="space-y-4" data-testid="attendance-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 text-sm" data-testid="attendance-totals">
                    <Badge variant="outline" data-testid="attendance-total-voices">
                        {t('attendance.total_voices', { count: sheet.total_voices })}
                    </Badge>
                    <Badge variant="outline" data-testid="attendance-total-members">
                        {t('attendance.total_members', { count: sheet.total_members })}
                    </Badge>
                    {frozen && <Badge variant="warning">{t('attendance.frozen')}</Badge>}
                </div>
                <Can perm="contracts.write">
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={frozen || generateAttendance.isPending}
                        onClick={() => (sheet.parties.length > 0 ? setPreviewOpen(true) : generateAttendance.mutate({ params: { path } }))}
                        data-testid="attendance-generate"
                    >
                        {t('attendance.generate')}
                    </Button>
                </Can>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="attendance-error">{error}</p>}

            {base.declared_voices != null && base.gap !== 0 && (
                <Alert variant={base.waived ? 'default' : 'warning'} data-testid="attendance-base-gap">
                    <AlertTitle data-testid="attendance-base-gap-title">
                        {t('attendance.base.title', { sheet: base.sheet_voices, declared: base.declared_voices })}
                    </AlertTitle>
                    <AlertDescription className="space-y-2">
                        <p>{t('attendance.base.help')}</p>
                        {base.waived && base.waiver ? (
                            <p data-testid="attendance-base-waived">{t('attendance.base.waived', { reason: base.waiver.reason })}</p>
                        ) : (
                            <Can perm="contracts.write">
                                <div className="flex flex-wrap items-end gap-2">
                                    <Input
                                        className="w-full max-w-md"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder={t('attendance.base.reason_placeholder')}
                                        data-testid="attendance-base-reason"
                                    />
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={!reason.trim() || waiveBase.isPending}
                                        onClick={() => waiveBase.mutate({ params: { path }, body: { reason: reason.trim() } }, { onSuccess: () => setReason('') })}
                                        data-testid="attendance-base-waive"
                                    >
                                        {t('attendance.base.waive')}
                                    </Button>
                                </div>
                            </Can>
                        )}
                    </AlertDescription>
                </Alert>
            )}

            {sheet.anomalies.length > 0 && (
                <Card data-testid="attendance-anomalies">
                    <CardHeader>
                        <CardTitle className="text-base">{t('attendance.anomalies_title')}</CardTitle>
                        <p className="text-sm text-muted-foreground">{t('attendance.anomalies_help')}</p>
                    </CardHeader>
                    <CardContent>
                        <ul className="space-y-1 text-sm">
                            {sheet.anomalies.map((a) => (
                                <li key={a.asset_uid} data-testid={`attendance-anomaly-${a.kind}`}>
                                    <Link
                                        to="/$org/assets/$assetUid"
                                        params={{ org, assetUid: a.asset_uid }}
                                        className="font-medium underline-offset-2 hover:underline"
                                        data-testid={`attendance-anomaly-link-${a.asset_uid}`}
                                    >
                                        {a.asset_name || a.asset_uid}
                                    </Link>
                                    : {t(`attendance.anomaly.${a.kind}`)}
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            )}

            {sheet.parties.length === 0 ? (
                <EmptyState variant="panel" title={t('attendance.empty.title')} description={t('attendance.empty.description')} data-testid="attendance-empty" />
            ) : (
                <Card>
                    <CardContent className="divide-y p-0" data-testid="attendance-list">
                        {sheet.parties.map((p) => (
                            <div key={p.stakeholder_uid} className="grid grid-cols-[1fr_6rem_10rem_auto] max-sm:grid-cols-1 items-center gap-3 px-4 py-2" data-testid={`attendance-row-${p.user_uid}`}>
                                <div className="min-w-0">
                                    <div className="truncate font-medium">{p.user_name || p.user_uid}</div>
                                    <div className="truncate text-xs text-muted-foreground">
                                        {p.lots.map((l) => `${l.asset_name ?? ''} (${l.voices})`).join(', ')}
                                    </div>
                                </div>
                                <div className="text-right tabular-nums" data-testid={`attendance-voices-${p.user_uid}`}>{p.voices}</div>
                                <select
                                    aria-label={t('attendance.presence_label')}
                                    value={p.presence ?? ''}
                                    disabled={status === 'closed'}
                                    onChange={(e) =>
                                        e.target.value &&
                                        setPresence.mutate({
                                            params: { path: { ...path, stakeholderUid: p.stakeholder_uid } },
                                            body: { presence: e.target.value as (typeof PRESENCES)[number] },
                                        })
                                    }
                                    className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                                    data-testid={`attendance-presence-${p.user_uid}`}
                                >
                                    <option value="">{t('attendance.presence_unset')}</option>
                                    {PRESENCES.map((v) => (
                                        <option key={v} value={v}>{t(`attendance.presence.${v}`)}</option>
                                    ))}
                                </select>
                                <Can perm="contracts.write">
                                    <div className="flex justify-end">
                                    {convened && status === 'open' && (
                                        <Button variant="ghost" size="sm" onClick={() => setHolderOf(p.stakeholder_uid)} data-testid={`attendance-change-holder-${p.user_uid}`}>
                                            {t('attendance.change_holder')}
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={frozen}
                                        onClick={() => setRemoveTarget({ uid: p.stakeholder_uid, name: p.user_name || p.user_uid })}
                                        data-testid={`attendance-remove-${p.user_uid}`}
                                    >
                                        {t('attendance.remove')}
                                    </Button>
                                    </div>
                                </Can>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            <Can perm="contracts.write">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">{t('attendance.add_title')}</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-wrap items-end gap-3">
                        <div className="w-72">
                            <UserSelect org={org} value={userUid} onValueChange={setUserUid} disabled={frozen} data-testid="attendance-add-user" />
                        </div>
                        <Input
                            type="number"
                            min={1}
                            className="w-32"
                            value={voices}
                            onChange={(e) => setVoices(e.target.value)}
                            placeholder={t('attendance.voices')}
                            disabled={frozen}
                            data-testid="attendance-add-voices"
                        />
                        <Button
                            size="sm"
                            disabled={frozen || !userUid || !(Number(voices) > 0) || addParty.isPending}
                            onClick={() =>
                                addParty.mutate(
                                    { params: { path }, body: { user_uid: userUid, voices: Number(voices) } },
                                    { onSuccess: () => { setUserUid(''); setVoices('') } },
                                )
                            }
                            data-testid="attendance-add"
                        >
                            {onSheet ? t('attendance.edit_voices') : t('attendance.add')}
                        </Button>
                    </CardContent>
                </Card>
            </Can>

            <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
                <AlertDialogContent data-testid="attendance-remove-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('attendance.remove_title', { name: removeTarget?.name })}</AlertDialogTitle>
                        <AlertDialogDescription>{t('attendance.remove_help')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => removeTarget && removeParty.mutate({ params: { path: { ...path, stakeholderUid: removeTarget.uid } } })}
                            data-testid="attendance-remove-confirm"
                        >
                            {t('attendance.remove')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent data-testid="attendance-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('attendance.preview_title')}</DialogTitle>
                    </DialogHeader>
                    {!preview ? (
                        <p className="text-sm text-muted-foreground">{t('loading')}</p>
                    ) : preview.lines.length === 0 ? (
                        <p className="text-sm" data-testid="attendance-preview-none">{t('attendance.preview_none')}</p>
                    ) : (
                        <div className="space-y-2 text-sm">
                            <p className="text-muted-foreground">{t('attendance.preview_help')}</p>
                            <ul className="divide-y rounded-md border" data-testid="attendance-preview-lines">
                                {preview.lines.map((l) => (
                                    <li key={l.user_uid} className="flex items-center justify-between gap-3 px-3 py-1.5" data-testid={`attendance-preview-${l.user_uid}`}>
                                        <span className="min-w-0 truncate">{l.user_name || l.user_uid}</span>
                                        <span className="tabular-nums">{l.before} → {l.after}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="font-medium tabular-nums" data-testid="attendance-preview-total">
                                {t('attendance.preview_total', { before: preview.total_before, after: preview.total_after })}
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewOpen(false)}>{t('cancel')}</Button>
                        <Button
                            disabled={!preview || generateAttendance.isPending}
                            onClick={() => generateAttendance.mutate({ params: { path } }, { onSuccess: () => setPreviewOpen(false) })}
                            data-testid="attendance-preview-confirm"
                        >
                            {t('attendance.generate')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={!!holderOf}
                onOpenChange={(o) => {
                    if (!o) {
                        setHolderOf(null)
                        setNewHolder('')
                        changeHolder.reset()
                    }
                }}
            >
                <DialogContent data-testid="change-holder-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('attendance.change_holder_title')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">{t('attendance.change_holder_help')}</p>
                        <UserSelect org={org} value={newHolder} onValueChange={setNewHolder} data-testid="change-holder-user" />
                        {holderError && <p className="text-sm text-destructive" data-testid="change-holder-error">{holderError}</p>}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setHolderOf(null)}>{t('cancel')}</Button>
                        <Button
                            disabled={!newHolder || changeHolder.isPending}
                            onClick={() =>
                                changeHolder.mutate(
                                    { params: { path: { ...path, stakeholderUid: holderOf! } }, body: { user_uid: newHolder } },
                                    { onSuccess: () => { setHolderOf(null); setNewHolder('') } },
                                )
                            }
                            data-testid="change-holder-confirm"
                        >
                            {t('attendance.change_holder')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
